/**
 * useBulkTicketActions - Select mode and bulk operations for the ticket list
 *
 * Extracted from useAdminReports.js. Owns:
 * - Select mode and per-report selection state
 * - Bulk close/assign via /items/bulk
 * - Analyst list for the bulk-assign dropdown (fetched on entering select mode)
 *
 * @param {string} organization - Base organization from route params
 * @param {Object} deps
 * @param {Array} deps.reports - Current report list (for selectAll)
 * @param {Function} deps.refresh - Refresh reports and counts after a bulk action
 * @param {Function} deps.handleError - Error handler from useErrorState
 */

import { useState, useEffect, useCallback } from 'react';
import { isWeb } from '../util/platform';
import { Alert } from 'react-native'

import Util from '../util/util.js';
import { showSuccess } from '../util/error';
import { t } from '../i18n';

export default function useBulkTicketActions(organization, { reports, refresh, handleError }) {
  const [selectMode, setSelectMode] = useState(false);
  const [selectedUuids, setSelectedUuids] = useState([]);
  const [analysts, setAnalysts] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkMessage, setBulkMessage] = useState(null);

  const toggleSelectMode = useCallback(() => {
    setSelectMode((prev) => {
      if (prev) setSelectedUuids([]);
      return !prev;
    });
  }, []);

  /** Leave select mode and drop the selection (e.g. when the ticket view changes) */
  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelectedUuids([]);
  }, []);

  const toggleSelect = useCallback((uuid) => {
    setSelectedUuids((prev) =>
      prev.includes(uuid) ? prev.filter((id) => id !== uuid) : [...prev, uuid]
    );
  }, []);

  const selectAll = useCallback(() => {
    setSelectedUuids(reports.map((r) => r.uuid));
  }, [reports]);

  const clearSelection = useCallback(() => {
    setSelectedUuids([]);
  }, []);

  const fetchAnalysts = useCallback(async () => {
    try {
      const response = await Util.get('UserManagement', '/users', {
        queryParams: { organization },
      });
      const rolesArray = response?.data?.roles || response?.roles;
      if (rolesArray && Array.isArray(rolesArray)) {
        const analystUsers = rolesArray
          .filter((userRole) => userRole.Roles && userRole.Roles.includes('Analyst'))
          .map((userRole) => ({ email: userRole.user }));
        setAnalysts(analystUsers);
      }
    } catch (err) {
      console.warn('Failed to fetch analysts:', err);
    }
  }, [organization]);

  const bulkAction = useCallback(async (action, params = {}) => {
    if (selectedUuids.length === 0) return;

    const count = selectedUuids.length;
    // One whole-sentence key per action rather than a gerund concatenated onto
    // a count phrase — verb and noun do not order the same way in every language.
    const actionKeys = { close: 'closing', status: 'updating', assign: 'assigning' };
    const actionKey = actionKeys[action] || 'updating';
    setBulkMessage(t(`admin:bulk.${actionKey}`, { count }));
    setBulkLoading(true);
    try {
      const body = {
        action,
        uuids: selectedUuids,
        organization,
        ...params,
      };

      const result = await Util.patch('Resty', '/items/bulk', { body });

      const { summary } = result;
      if (summary.failed > 0) {
        showSuccess(t('admin:bulk.updatedSummary', {
          succeeded: summary.succeeded,
          total: summary.total,
          failed: summary.failed,
        }));
      } else {
        showSuccess(t('admin:bulk.updated', { count: summary.succeeded }));
      }

      // Exit select mode and refresh
      setSelectMode(false);
      setSelectedUuids([]);
      refresh();
    } catch (err) {
      handleError(err, { userMessage: t('admin:bulk.failed') });
    } finally {
      setBulkLoading(false);
      setBulkMessage(null);
    }
  }, [selectedUuids, organization, refresh, handleError]);

  const bulkClose = useCallback(() => {
    const doClose = () => bulkAction('close', { status: 'Closed' });

    if (isWeb()) {
      if (window.confirm(t('admin:bulk.confirmClose', { count: selectedUuids.length }))) {
        doClose();
      }
    } else {
      Alert.alert(
        t('admin:bulk.closeReportsTitle'),
        t('admin:bulk.confirmClose', { count: selectedUuids.length }),
        [
          { text: t('common:actions.cancel'), style: 'cancel' },
          { text: t('admin:bulk.close'), onPress: doClose },
        ]
      );
    }
  }, [selectedUuids.length, bulkAction]);

  const bulkAssign = useCallback((assignedTo) => {
    bulkAction('assign', { assignedTo });
  }, [bulkAction]);

  // Fetch analysts when entering select mode (for bulk assign)
  useEffect(() => {
    if (selectMode && analysts.length === 0) {
      fetchAnalysts();
    }
  }, [selectMode, analysts.length, fetchAnalysts]);

  return {
    selectMode,
    selectedUuids,
    analysts,
    bulkLoading,
    bulkMessage,
    toggleSelectMode,
    exitSelectMode,
    toggleSelect,
    selectAll,
    clearSelection,
    bulkClose,
    bulkAssign,
  };
}
